import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import type { TFunction } from 'i18next'
import { Line } from 'react-chartjs-2'
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js'
import { X } from 'lucide-react'
import LoadingSpinner from '../../components/LoadingSpinner'
import ErrorMessage from '../../components/ErrorMessage'
import ExportButtons from '../../components/ExportButtons'
import type { ColumnDefinition } from '../../utils/exportToCsv'
import { bruktbilfinansieringAPI } from '../../services/api'
import type { BruktbilfinansieringFilters } from './hooks/useBruktbilfinansiering'
import type { CarAgeBucketResponse, CarAgeBucketRow } from '../../types/bruktbilfinansiering'
import BruktbilfinansieringInfoBox from './BruktbilfinansieringInfoBox'
import { KNOWN_BANKS } from './knownBanks'

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler)

const MAX_COMPARE_BANKS = 5

const COMPARISON_COLORS = [
  { line: 'rgba(239, 68, 68, 1)',  fill: 'rgba(239, 68, 68, 0.05)' },
  { line: 'rgba(16, 185, 129, 1)', fill: 'rgba(16, 185, 129, 0.05)' },
  { line: 'rgba(245, 158, 11, 1)', fill: 'rgba(245, 158, 11, 0.05)' },
  { line: 'rgba(139, 92, 246, 1)', fill: 'rgba(139, 92, 246, 0.05)' },
  { line: 'rgba(236, 72, 153, 1)', fill: 'rgba(236, 72, 153, 0.05)' },
]
const SELF_COLOR = { line: 'rgba(8, 145, 178, 1)', fill: 'rgba(8, 145, 178, 0.15)' }   // primary-600 cyan

type SystemMetric =
  | 'laneandel'
  | 'antall_biler'
  | 'volum'
  | 'bankens_lan_share'    // bucket's share of user-bank's total loan count
  | 'bankens_volum_share'  // bucket's share of user-bank's total volume

type PerBankMetric =
  | 'market_share'
  | 'loan_count'
  | 'own_count_share'      // share of EACH bank's own total loan count
  | 'own_volume_share'     // share of EACH bank's own total volume

interface Props {
  bankOrgNr: string
  bankName: string
  filters: BruktbilfinansieringFilters
}

const _nb = new Intl.NumberFormat('nb-NO')
const _nb0 = new Intl.NumberFormat('nb-NO', { maximumFractionDigits: 0 })
const fmtNum = (v: number) => _nb.format(v)
const fmtVolum = (v: number) => _nb0.format(v)
// Grid-only: percent without "%" suffix, one decimal.
const fmtPctCell = (v: number) =>
  v.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })

const exportColumns = (t: TFunction<'bbf'>): ColumnDefinition[] => [
  { key: 'bucket_label', label: t('shared.carAge') },
  { key: 'antall_biler', label: t('bilalder.cars') },
  { key: 'antall_lan', label: t('shared.loans') },
  { key: 'laneandel', label: t('bilalder.export.loanSharePct'), format: (v) => v != null ? (Number(v) * 100).toFixed(1) : '' },
  { key: 'volum', label: t('shared.volume'), format: (v) => v != null ? Number(v).toFixed(0) : '' },
  { key: 'bankens_lan', label: t('shared.bankLoans') },
  { key: 'bankens_volum', label: t('bilalder.export.bankVolume'), format: (v) => v != null ? Number(v).toFixed(0) : '' },
  { key: 'bankens_markedsandel', label: t('bilalder.export.bankMarketSharePct'), format: (v) => v != null ? (Number(v) * 100).toFixed(1) : '' },
  { key: 'bankens_lan_share', label: t('bilalder.export.shareOfBankLoansPct'), format: (v) => v != null ? (Number(v) * 100).toFixed(1) : '' },
  { key: 'bankens_volum_share', label: t('bilalder.export.shareOfBankVolumePct'), format: (v) => v != null ? (Number(v) * 100).toFixed(1) : '' },
]

export default function BilalderTab({ bankOrgNr, bankName, filters }: Props) {
  const { t } = useTranslation('bbf')
  const [data, setData] = useState<CarAgeBucketResponse | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [compareBanks, setCompareBanks] = useState<{ org_nr: string; name: string }[]>([])
  const [systemMetric, setSystemMetric] = useState<SystemMetric>('laneandel')
  const [perBankMetric, setPerBankMetric] = useState<PerBankMetric>('market_share')

  const compareKey = compareBanks.map(b => b.org_nr).sort().join(',')
  const showComparison = compareBanks.length > 0

  useEffect(() => {
    setLoading(true)
    setError(null)
    bruktbilfinansieringAPI.getCarAgeBuckets(bankOrgNr, {
      start_date: filters.startDate,
      end_date: filters.endDate,
      dealer_postnummer: filters.dealerPostnummer ?? undefined,
      buyer_postnummer: filters.buyerPostnummer ?? undefined,
      varebil: filters.varebil,
      classes: filters.classes?.length ? filters.classes : undefined,
      compare_bank_org_nrs: compareBanks.length ? compareBanks.map(b => b.org_nr) : undefined,
    })
      .then(res => setData(res.data))
      .catch(e => setError(e instanceof Error ? e.message : t('errors.carAge')))
      .finally(() => setLoading(false))
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bankOrgNr, filters.startDate, filters.endDate, filters.dealerPostnummer, filters.buyerPostnummer, filters.varebil, JSON.stringify(filters.classes), compareKey])

  if (loading) return <LoadingSpinner />
  if (error) return <ErrorMessage message={error} />
  if (!data) return null

  const rows = data.rows
  const profiles = data.profiles ?? []
  const labels = rows.map(r => r.bucket_label)

  const totals = rows.reduce(
    (acc, r) => ({
      antall_biler: acc.antall_biler + r.antall_biler,
      antall_lan: acc.antall_lan + r.antall_lan,
      volum: acc.volum + Number(r.volum),
      bankens_lan: acc.bankens_lan + r.bankens_lan,
    }),
    { antall_biler: 0, antall_lan: 0, volum: 0, bankens_lan: 0 },
  )
  const totalLaneandel = totals.antall_biler > 0 ? totals.antall_lan / totals.antall_biler : 0
  const totalBankAndel = totals.antall_lan > 0 ? totals.bankens_lan / totals.antall_lan : 0

  const totalBankensVolum = rows.reduce((a, r) => a + Number(r.bankens_volum ?? 0), 0)
  const exportRows: (CarAgeBucketRow | Record<string, unknown>)[] = [
    {
      bucket_label: 'TOTAL', bucket_order: -1,
      antall_biler: totals.antall_biler,
      antall_lan: totals.antall_lan,
      laneandel: totalLaneandel,
      volum: totals.volum,
      bankens_lan: totals.bankens_lan,
      bankens_volum: totalBankensVolum,
      bankens_markedsandel: totalBankAndel,
      bankens_lan_share: 1,
      bankens_volum_share: 1,
    },
    ...rows,
  ]

  // -- Chart construction -----------------------------------------------------
  let chartTitle = ''
  let yIsPct = false
  let datasets: {
    label: string; data: number[]; borderColor: string; backgroundColor: string;
    fill: boolean; tension: number; pointRadius: number; pointHoverRadius: number; borderWidth: number
  }[] = []

  if (showComparison && profiles.length > 0) {
    yIsPct = perBankMetric !== 'loan_count'
    const PER_BANK_TITLE_KEYS = {
      market_share: 'bilalder.perBankTitles.market_share',
      loan_count: 'bilalder.perBankTitles.loan_count',
      own_count_share: 'bilalder.perBankTitles.own_count_share',
      own_volume_share: 'bilalder.perBankTitles.own_volume_share',
    } as const satisfies Record<PerBankMetric, string>
    chartTitle = t(PER_BANK_TITLE_KEYS[perBankMetric])
    datasets = profiles.map((p, idx) => {
      const palette = p.is_self ? SELF_COLOR : COMPARISON_COLORS[(idx - 1 + COMPARISON_COLORS.length) % COMPARISON_COLORS.length]
      const values = p.points.map(pt => {
        switch (perBankMetric) {
          case 'market_share':     return pt.market_share * 100
          case 'loan_count':       return pt.loan_count
          case 'own_count_share':  return pt.own_count_share * 100
          case 'own_volume_share': return pt.own_volume_share * 100
        }
      })
      return {
        label: p.bank_name,
        data: values,
        borderColor: palette.line,
        backgroundColor: p.is_self ? palette.fill : 'transparent',
        fill: p.is_self,
        tension: 0.3,
        pointRadius: 4,
        pointHoverRadius: 6,
        borderWidth: p.is_self ? 2.5 : 2,
      }
    })
  } else {
    // Single-line system-wide metric (the user's bank's view of total system)
    const getVal = (r: CarAgeBucketRow) => {
      switch (systemMetric) {
        case 'laneandel':            return r.laneandel * 100
        case 'antall_biler':         return r.antall_biler
        case 'volum':                return Number(r.volum)
        case 'bankens_lan_share':    return r.bankens_lan_share * 100
        case 'bankens_volum_share':  return r.bankens_volum_share * 100
      }
    }
    yIsPct = systemMetric === 'laneandel'
      || systemMetric === 'bankens_lan_share'
      || systemMetric === 'bankens_volum_share'
    const SYSTEM_TITLE_KEYS = {
      laneandel: 'bilalder.systemTitles.laneandel',
      antall_biler: 'bilalder.systemTitles.antall_biler',
      volum: 'bilalder.systemTitles.volum',
      bankens_lan_share: 'bilalder.systemTitles.bankens_lan_share',
      bankens_volum_share: 'bilalder.systemTitles.bankens_volum_share',
    } as const satisfies Record<SystemMetric, string>
    chartTitle = t(SYSTEM_TITLE_KEYS[systemMetric])
    datasets = [{
      label: chartTitle,
      data: rows.map(getVal),
      borderColor: SELF_COLOR.line,
      backgroundColor: SELF_COLOR.fill,
      fill: true,
      tension: 0.3,
      pointRadius: 5,
      pointHoverRadius: 7,
      borderWidth: 2,
    }]
  }

  return (
    <div className="space-y-4">
      <BruktbilfinansieringInfoBox bankName={bankName} filters={filters} tab="bilalder" />

      {/* Bank comparison picker */}
      <div className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-sm font-medium text-gray-700 dark:text-gray-300 mr-1">{t('shared.banksLabel')}</span>
          <span className="inline-flex items-center px-2 py-1 rounded-sm bg-primary-100 dark:bg-primary-900/30 text-primary-800 dark:text-primary-200 text-xs">
            {t('shared.you', { bank: bankName })}
          </span>
          {compareBanks.map(b => (
            <span key={b.org_nr} className="inline-flex items-center gap-1 px-2 py-1 rounded-sm bg-gray-100 dark:bg-gray-700 text-xs text-gray-700 dark:text-gray-200">
              {b.name}
              <button
                onClick={() => setCompareBanks(prev => prev.filter(x => x.org_nr !== b.org_nr))}
                className="hover:text-red-600"
                aria-label={t('shared.remove', { name: b.name })}
              >
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <CompareBankPicker
            existing={[bankOrgNr, ...compareBanks.map(b => b.org_nr)]}
            disabled={compareBanks.length >= MAX_COMPARE_BANKS}
            onAdd={b => setCompareBanks(prev => [...prev, b])}
          />
        </div>
      </div>

      <div className="flex justify-end">
        <ExportButtons
          data={exportRows}
          columns={exportColumns(t)}
          metadata={{ title: t('exportTitles.carAge', { bank: bankOrgNr }) }}
          disabled={loading}
        />
      </div>

      {rows.length === 0 ? (
        <p className="text-gray-500 dark:text-gray-400 text-center py-8">
          {t('shared.noDataPeriodDot')}
        </p>
      ) : (
        <>
        {/* Curve */}
        <div className="card p-4">
          <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
            <h3 className="text-sm font-medium text-gray-700 dark:text-gray-300">{chartTitle}</h3>
            <div className="inline-flex rounded-lg border border-gray-300 dark:border-gray-600 overflow-hidden text-xs">
              {showComparison ? (
                <>
                  <MetricButton active={perBankMetric === 'market_share'}     onClick={() => setPerBankMetric('market_share')}>{t('shared.marketShare')}</MetricButton>
                  <MetricButton active={perBankMetric === 'loan_count'}       onClick={() => setPerBankMetric('loan_count')}>{t('shared.loansCount')}</MetricButton>
                  <MetricButton active={perBankMetric === 'own_count_share'}  onClick={() => setPerBankMetric('own_count_share')}>{t('bilalder.ownLoansShare')}</MetricButton>
                  <MetricButton active={perBankMetric === 'own_volume_share'} onClick={() => setPerBankMetric('own_volume_share')}>{t('bilalder.ownVolumeShare')}</MetricButton>
                </>
              ) : (
                <>
                  <MetricButton active={systemMetric === 'laneandel'}            onClick={() => setSystemMetric('laneandel')}>{t('shared.loanShare')}</MetricButton>
                  <MetricButton active={systemMetric === 'antall_biler'}         onClick={() => setSystemMetric('antall_biler')}>{t('bilalder.carsCount')}</MetricButton>
                  <MetricButton active={systemMetric === 'volum'}                onClick={() => setSystemMetric('volum')}>{t('shared.volume')}</MetricButton>
                  <MetricButton active={systemMetric === 'bankens_lan_share'}    onClick={() => setSystemMetric('bankens_lan_share')}>{t('bilalder.bankLoansShare')}</MetricButton>
                  <MetricButton active={systemMetric === 'bankens_volum_share'}  onClick={() => setSystemMetric('bankens_volum_share')}>{t('bilalder.bankVolumeShare')}</MetricButton>
                </>
              )}
            </div>
          </div>
          <div style={{ height: 300 }}>
            <Line
              data={{ labels, datasets }}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                interaction: { mode: 'index' as const, intersect: false },
                plugins: {
                  legend: { display: showComparison, position: 'bottom' as const },
                  tooltip: {
                    callbacks: {
                      label: (ctx: { dataset: { label?: string }; parsed: { y: number | null } }) => {
                        const v = ctx.parsed.y ?? 0
                        const lbl = ctx.dataset.label ?? ''
                        return yIsPct
                          ? `${lbl}: ${v.toLocaleString('nb-NO', { minimumFractionDigits: 1, maximumFractionDigits: 1 })} %`
                          : `${lbl}: ${fmtNum(v)}`
                      },
                    },
                  },
                },
                scales: {
                  x: { title: { display: true, text: t('shared.carAge') } },
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: (val: string | number) => {
                        const n = Number(val)
                        return yIsPct ? `${n.toFixed(0)} %` : fmtNum(n)
                      },
                    },
                  },
                },
              }}
            />
          </div>
          {showComparison && (
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
              {t('bilalder.curveNote', { bank: bankName })}
            </p>
          )}
        </div>

        <div className="overflow-x-auto card p-0">
          <table className="data-table data-table-compact">
            <thead>
              <tr>
                <th className="text-left">{t('shared.carAge')}</th>
                <th className="text-right">{t('bilalder.cars')}</th>
                <th className="text-right">{t('shared.loans')}</th>
                <th className="text-right">{t('shared.loanShare')}</th>
                <th className="text-right">{t('shared.volume')}</th>
                <th className="text-right">{t('shared.bankLoans')}</th>
                <th className="text-right">{t('bilalder.bankMarketShare')}</th>
                <th className="text-right" title={t('bilalder.bankLoansShareTitle')}>{t('bilalder.bankLoansShare')}</th>
                <th className="text-right" title={t('bilalder.bankVolumeShareTitle')}>{t('bilalder.bankVolumeShare')}</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-primary-50 dark:bg-primary-900/20 font-bold border-t border-b border-primary-300 dark:border-primary-600">
                <td>∑ TOTAL</td>
                <td className="text-right tabular-nums">{fmtNum(totals.antall_biler)}</td>
                <td className="text-right tabular-nums">{fmtNum(totals.antall_lan)}</td>
                <td className="text-right tabular-nums">{fmtPctCell(totalLaneandel * 100)}</td>
                <td className="text-right tabular-nums">{fmtVolum(totals.volum)}</td>
                <td className="text-right tabular-nums">{fmtNum(totals.bankens_lan)}</td>
                <td className="text-right tabular-nums">{fmtPctCell(totalBankAndel * 100)}</td>
                <td className="text-right tabular-nums">{fmtPctCell(100)}</td>
                <td className="text-right tabular-nums">{fmtPctCell(100)}</td>
              </tr>
              {rows.map((row, idx) => (
                <tr
                  key={row.bucket_label}
                  className={`${idx % 2 === 0 ? 'bg-white dark:bg-gray-900/40' : 'bg-gray-50 dark:bg-gray-800/60'} hover:bg-gray-100 dark:hover:bg-dark-border transition-colors`}
                >
                  <td className="font-medium">{row.bucket_label}</td>
                  <td className="text-right tabular-nums">{fmtNum(row.antall_biler)}</td>
                  <td className="text-right tabular-nums">{fmtNum(row.antall_lan)}</td>
                  <td className="text-right tabular-nums">{fmtPctCell(row.laneandel * 100)}</td>
                  <td className="text-right tabular-nums">{fmtVolum(Number(row.volum))}</td>
                  <td className="text-right tabular-nums">{fmtNum(row.bankens_lan)}</td>
                  <td className="text-right tabular-nums">{fmtPctCell(row.bankens_markedsandel * 100)}</td>
                  <td className="text-right tabular-nums">{fmtPctCell(row.bankens_lan_share * 100)}</td>
                  <td className="text-right tabular-nums">{fmtPctCell(row.bankens_volum_share * 100)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        </>
      )}
    </div>
  )
}

function MetricButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: React.ReactNode }) {
  return (
    <button
      onClick={onClick}
      className={`px-3 py-1.5 transition-colors ${
        active
          ? 'bg-primary-600 text-white'
          : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
      }`}
    >
      {children}
    </button>
  )
}

function CompareBankPicker({ existing, disabled, onAdd }: {
  existing: string[]
  disabled: boolean
  onAdd: (b: { org_nr: string; name: string }) => void
}) {
  const { t } = useTranslation('bbf')
  const available = KNOWN_BANKS.filter(b => !existing.includes(b.org_nr))
  return (
    <select
      disabled={disabled || available.length === 0}
      value=""
      onChange={e => {
        const b = available.find(x => x.org_nr === e.target.value)
        if (b) onAdd(b)
      }}
      className="text-xs px-2 py-1 rounded-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 disabled:opacity-50"
    >
      <option value="">{t('shared.addComparison')}</option>
      {available.map(b => (
        <option key={b.org_nr} value={b.org_nr}>{b.name}</option>
      ))}
    </select>
  )
}
